import type { CSSProperties, FC, ReactNode } from 'react'
import type { HeroListing } from '@/components/home/market-hero/hero-market-data'

import { BadgeCheck, Menu, ShieldCheck } from 'lucide-react'

import { cn } from '@/lib/utils'
import { HERO_PHONE_LISTING, TONE_PLATE } from '@/components/home/market-hero/hero-market-data'

/**
 * The device at the centre of the hero, built entirely from HTML and CSS.
 *
 * There is no picture of a phone anywhere in this component, which is the
 * point: the screen holds a REAL Bazio listing card, so it scales, re-flows
 * and stays sharp at every density instead of going soft the way a screenshot
 * does. Like the floating cards it is sized purely in `em`, so the whole
 * device is scaled by the single `font-size` its piece carries.
 *
 * The 3D turn lives HERE rather than on a parent, because the parents are
 * already spending their own transforms on parallax, entrance and drift, and
 * an element can only hold one transform at a time.
 */

/** One gradient, doing one job: the metal rail catching light down two edges. */
const RAIL: CSSProperties = {
    background: 'linear-gradient(150deg, #4c4a57 0%, #0d0b14 20%, #0d0b14 80%, #3a3842 100%)',
    boxShadow: '0 26px 50px -24px rgba(13, 11, 20, 0.55)',
    transform: 'perspective(1500px) rotateY(-6deg) rotateX(1.5deg)'
}

interface MarketPhoneProps {
    listing?: HeroListing
    className?: string
}

export const MarketPhone: FC<MarketPhoneProps> = ({
    listing = HERO_PHONE_LISTING,
    className
}): ReactNode => {
    const Icon = listing.icon

    return (
        <div
            style={RAIL}
            className={cn('relative aspect-[0.485/1] w-full rounded-[2em] p-[0.3em]', className)}
        >
            {/* ---- Side rails. Decorative hardware, so no accessible name. ---- */}
            <span className='absolute -left-[0.12em] top-[19%] h-[1.5em] w-[0.12em] rounded-l-[0.06em] bg-[#2f2d38]' />
            <span className='absolute -left-[0.12em] top-[28%] h-[2.6em] w-[0.12em] rounded-l-[0.06em] bg-[#2f2d38]' />
            <span className='absolute -right-[0.12em] top-[31%] h-[3.4em] w-[0.12em] rounded-r-[0.06em] bg-[#2f2d38]' />

            <div className='relative h-full w-full overflow-hidden rounded-[1.75em] bg-paper'>
                {/* ---- Dynamic island ---- */}
                <div className='absolute left-1/2 top-[0.5em] z-10 flex h-[1.3em] w-[4.3em] -translate-x-1/2 items-center justify-end rounded-full bg-ink-900 pr-[0.42em]'>
                    <span className='size-[0.4em] rounded-full bg-[#1d2a3a]' />
                </div>

                <div className='flex h-full flex-col px-[0.75em] pb-[0.75em] pt-[2.3em]'>
                    {/* ---- App bar ---- */}
                    <div className='flex shrink-0 items-center justify-between'>
                        <p className='text-[0.86em] font-extrabold leading-none tracking-[-0.02em] text-ink-900'>
                            <span className='text-brand-red'>Bazio</span> Market
                        </p>

                        <Menu className='size-[0.95em] text-ink-900' strokeWidth={2.8} aria-hidden='true' />
                    </div>

                    {/* ---- The listing ---- */}
                    {/*
                        `justify-between` rather than a stretching media plate.
                        The phone is always about 14em wide (each breakpoint's
                        `fs` is tuned to keep it there), so the card's content
                        is a known height and the leftover space reads as
                        deliberate air instead of one enormous square. The
                        plate is the only item allowed to shrink, so a tight
                        box squeezes the artwork rather than clipping the price.
                    */}
                    <div className='mt-[0.7em] flex min-h-0 flex-1 flex-col justify-between gap-[0.55em] rounded-[0.9em] border-2 border-ink-900 bg-canvas p-[0.55em]'>
                        <div
                            className='relative grid aspect-[1/1.1] w-full min-h-0 place-items-center rounded-[0.65em] border-2 border-ink-900'
                            style={{ backgroundColor: TONE_PLATE[listing.tone] }}
                        >
                            {listing.flag ? (
                                <span className='hero-label absolute -left-[0.3em] -top-[0.35em] rounded-[0.32em] border-2 border-ink-900 bg-brand-red px-[0.5em] py-[0.08em] text-[0.6em] leading-[1.6] tracking-[0.06em] text-white'>
                                    Hot
                                </span>
                            ) : null}

                            <span className='grid aspect-square w-[42%] place-items-center rounded-[0.9em] bg-ink-900'>
                                <Icon className='size-[1.9em] text-paper' strokeWidth={2.2} aria-hidden='true' />
                            </span>
                        </div>

                        <div className='shrink-0'>
                            <p className='truncate text-[0.86em] font-bold leading-[1.25] text-ink-900'>
                                {listing.label}
                            </p>

                            <p className='truncate text-[0.7em] font-medium leading-[1.35] text-text-muted'>
                                {listing.detail}
                            </p>
                        </div>

                        <div className='flex shrink-0 items-end justify-between gap-[0.4em]'>
                            <p className='text-[1.5em] font-extrabold leading-none tracking-[-0.03em] text-ink-900'>
                                {listing.price}
                            </p>

                            <span className='flex min-w-0 items-center gap-[0.25em]'>
                                <span className='truncate text-[0.66em] font-semibold leading-[1.4] text-text-muted'>
                                    {listing.handle}
                                </span>

                                <BadgeCheck
                                    className='size-[0.78em] shrink-0 text-verify-blue'
                                    strokeWidth={2.6}
                                    aria-hidden='true'
                                />
                            </span>
                        </div>

                        <div className='shrink-0 border-t-2 border-dashed border-ink-900/15 pt-[0.45em]'>
                            <span className='flex items-center gap-[0.3em]'>
                                <ShieldCheck
                                    className='size-[0.85em] shrink-0 text-verify-blue-ink'
                                    strokeWidth={2.6}
                                    aria-hidden='true'
                                />

                                <span className='text-[0.66em] font-bold leading-none text-ink-900'>
                                    Verified seller
                                </span>
                            </span>
                        </div>
                    </div>

                    {/* ---- Home indicator ---- */}
                    <span className='mx-auto mt-[0.6em] block h-[0.2em] w-[35%] shrink-0 rounded-full bg-ink-900/70' />
                </div>
            </div>
        </div>
    )
}
